import React from "react";

const getImageUrl = (img) => {
  if (!img) return "";
  if (
    img.startsWith("data:") ||
    img.startsWith("http://") ||
    img.startsWith("https://") ||
    img.startsWith("blob:") ||
    (img.startsWith("/") && img.length < 1000) ||
    img.startsWith("static/") ||
    img.startsWith("assets/")
  ) {
    return img;
  }
  return `data:image/jpeg;base64,${img}`;
};

export default function VerifiedReportsModal({ isOpen, onClose, tickets }) {
  const [selectedCompare, setSelectedCompare] = React.useState(null);

  React.useEffect(() => {
    if (isOpen && tickets.length > 0) {
      const resolvedIds = tickets.filter(t => t.status === "Resolved").map(t => t.ticket_id);
      const existingSeen = JSON.parse(localStorage.getItem("seen_tickets") || "[]");
      const newSeen = Array.from(new Set([...existingSeen, ...resolvedIds]));
      localStorage.setItem("seen_tickets", JSON.stringify(newSeen));
    }
  }, [isOpen, tickets]);

  if (!isOpen) return null;

  const resolved = tickets.filter((t) => t.status === "Resolved");
  const pending = tickets.filter((t) => t.status === "Pending");

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-surface-container-high dark:bg-surface-container-high border border-outline-variant/30 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden text-on-surface">
        {/* Header */}
        <div className="px-6 py-4 border-b border-outline-variant/20 flex items-center justify-between bg-surface-container-lowest shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-[24px]">verified</span>
            </div>
            <div>
              <h3 className="font-headline-md text-headline-md font-bold text-on-surface">
                Agronomist Verified Reports
              </h3>
              <p className="text-xs text-on-surface-variant font-medium">
                Official diagnostic confirmations and treatment advice from Agronomists
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-on-surface-variant hover:bg-surface-container-highest transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {tickets.length === 0 ? (
            <div className="py-12 text-center text-on-surface-variant">
              <span className="material-symbols-outlined text-[48px] opacity-40 mb-2">find_in_page</span>
              <p className="font-semibold text-sm">No verification requests found.</p>
              <p className="text-xs mt-1 opacity-70">When you send a scan for expert verification, reports will appear here.</p>
            </div>
          ) : (
            <>
              {/* Resolved Tickets */}
              {resolved.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-secondary mb-3 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[16px]">check_circle</span>
                    Verified Reports ({resolved.length})
                  </h4>
                  <div className="space-y-4">
                    {resolved.map((t) => (
                      <TicketCard key={t.ticket_id} ticket={t} onSelect={() => setSelectedCompare(t)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Verification Requests */}
              {pending.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-3 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[16px]">schedule</span>
                    Pending Review ({pending.length})
                  </h4>
                  <div className="space-y-4">
                    {pending.map((t) => (
                      <TicketCard key={t.ticket_id} ticket={t} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-lowest flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="py-2.5 px-6 bg-surface-container text-on-surface hover:bg-surface-container-highest font-bold rounded-xl text-sm transition-all"
          >
            Close
          </button>
        </div>
      </div>

      {/* Comparison Sub-Modal */}
      {selectedCompare && (
        <div
          className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setSelectedCompare(null)}
        >
          <div
            className="bg-surface text-on-surface w-full max-w-4xl rounded-3xl shadow-2xl border border-outline-variant/30 flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-outline-variant/20 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-[20px]">compare</span>
                </div>
                <div>
                  <h3 className="font-bold text-base text-on-surface">Report Comparison</h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">Ticket ID: {selectedCompare.ticket_id}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCompare(null)}
                className="p-2 hover:bg-surface-container-high rounded-full transition-colors"
              >
                <span className="material-symbols-outlined text-[22px] text-on-surface-variant">close</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col md:flex-row gap-6">
              {/* Left: Image & Recs */}
              <div className="flex-1 flex flex-col gap-4">
                <div className="w-full aspect-video rounded-2xl border border-outline-variant/30 overflow-hidden bg-black flex items-center justify-center shadow-inner">
                  {selectedCompare.image ? (
                    <img src={getImageUrl(selectedCompare.image)} alt="Crop Scan" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="material-symbols-outlined text-[48px] text-white/30">image_not_supported</span>
                  )}
                </div>
                {selectedCompare.validated_prediction?.recommendations && (
                  <div className="bg-surface-container p-4 rounded-xl border border-outline-variant/20">
                    <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider mb-1">
                      Expert Recommendations
                    </span>
                    <p className="text-sm leading-relaxed text-on-surface whitespace-pre-line">
                      {selectedCompare.validated_prediction.recommendations}
                    </p>
                  </div>
                )}
              </div>

              {/* Right: Side-by-Side Comparison */}
              <div className="w-full md:w-[400px] shrink-0 flex flex-col justify-center">
                {(() => {
                  const orig = selectedCompare.original_prediction || {};
                  const val = selectedCompare.validated_prediction || {};

                  const origCrop = orig.crop?.label || orig.crop || "undefined";
                  const valCrop = val.crop || "undefined";

                  const origStage = orig.stage?.label || orig.stage || "None";
                  const valStage = val.stage || "None";

                  const origDisease = orig.pest?.disease || orig.disease || "None";
                  const valDisease = val.disease || "None";

                  const origPest = orig.pest?.pest || orig.pest || "None";
                  const valPest = val.pest || "None";

                  const origCategory = orig.pest?.category || orig.category || "None";
                  const valCategory = val.category || "None";

                  const changesCrop = origCrop !== valCrop;
                  const changesStage = origStage !== valStage;
                  const changesDisease = origDisease !== valDisease;
                  const changesPest = origPest !== valPest;
                  const changesCategory = origCategory !== valCategory;

                  const changesMade = changesCrop || changesStage || changesDisease || changesPest || changesCategory;

                  if (!changesMade) {
                    return (
                      <div className="bg-primary/5 p-6 rounded-2xl border border-primary/20 text-center flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                          <span className="material-symbols-outlined text-[28px]">verified</span>
                        </div>
                        <h4 className="font-bold text-lg text-on-surface">Prediction is Accurate</h4>
                        <p className="text-sm text-on-surface-variant leading-relaxed">
                          No changes were made by the agronomist. The model's prediction is accurate.
                        </p>
                        <div className="w-full mt-2 space-y-3 text-left border-t border-outline-variant/20 pt-4">
                          <div>
                            <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider">Crop</span>
                            <span className="font-bold text-sm text-on-surface">{valCrop}</span>
                          </div>
                          <div>
                            <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider">Stage</span>
                            <span className="font-bold text-sm text-on-surface">{valStage}</span>
                          </div>
                          <div>
                            <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider">Disease</span>
                            <span className="font-bold text-sm text-on-surface">{valDisease}</span>
                          </div>
                          <div>
                            <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider">Pest</span>
                            <span className="font-bold text-sm text-on-surface">{valPest}</span>
                          </div>
                          <div>
                            <span className="text-[10px] text-on-surface-variant block uppercase font-bold tracking-wider">Category</span>
                            <span className="font-bold text-sm text-on-surface">{valCategory}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="flex flex-col gap-4">
                      <div className="bg-secondary/5 p-4 rounded-xl border border-secondary/20 flex items-center gap-2">
                        <span className="material-symbols-outlined text-secondary">published_with_changes</span>
                        <span className="text-sm font-semibold text-secondary">Expert updates have been applied</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Original */}
                        <div className="bg-surface-container-low p-4 rounded-xl border border-outline-variant/20 flex flex-col gap-3">
                          <h4 className="font-bold text-xs uppercase tracking-wider text-error flex items-center gap-1">
                            <span className="material-symbols-outlined text-[14px]">smart_toy</span>
                            Original
                          </h4>
                          <div className="space-y-3 text-xs">
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Crop</span>
                              <span className="font-bold text-on-surface">{origCrop}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Stage</span>
                              <span className="font-bold text-on-surface">{origStage}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Disease</span>
                              <span className="font-bold text-on-surface">{origDisease}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Pest</span>
                              <span className="font-bold text-on-surface">{origPest}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Category</span>
                              <span className="font-bold text-on-surface">{origCategory}</span>
                            </div>
                          </div>
                        </div>

                        {/* Verified */}
                        <div className="bg-secondary/5 p-4 rounded-xl border border-secondary/20 flex flex-col gap-3">
                          <h4 className="font-bold text-xs uppercase tracking-wider text-secondary flex items-center gap-1">
                            <span className="material-symbols-outlined text-[14px]">verified</span>
                            Verified
                          </h4>
                          <div className="space-y-3 text-xs">
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Crop</span>
                              <span className={`font-bold ${changesCrop ? "text-secondary font-extrabold" : "text-on-surface"}`}>{valCrop}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Stage</span>
                              <span className={`font-bold ${changesStage ? "text-secondary font-extrabold" : "text-on-surface"}`}>{valStage}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Disease</span>
                              <span className={`font-bold ${changesDisease ? "text-secondary font-extrabold" : "text-on-surface"}`}>{valDisease}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Pest</span>
                              <span className={`font-bold ${changesPest ? "text-secondary font-extrabold" : "text-on-surface"}`}>{valPest}</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-on-surface-variant block uppercase font-bold tracking-wider">Category</span>
                              <span className={`font-bold ${changesCategory ? "text-secondary font-extrabold" : "text-on-surface"}`}>{valCategory}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-outline-variant/20 bg-surface-container-lowest flex justify-end shrink-0">
              <button
                onClick={() => setSelectedCompare(null)}
                className="py-2 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition-all"
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketCard({ ticket, onSelect }) {
  const isResolved = ticket.status === "Resolved";
  const orig = ticket.original_prediction || {};

  return (
    <div
      onClick={isResolved ? onSelect : undefined}
      className={`p-4 rounded-xl border transition-all ${isResolved
          ? "bg-surface-container-lowest border-outline-variant/30 hover:border-primary/50 hover:bg-surface-container/20 cursor-pointer shadow-sm"
          : "bg-surface-container-lowest border-outline-variant/30 opacity-75"
        }`}
    >
      {/* Top Banner */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-outline-variant/10">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-bold text-primary">{ticket.ticket_id}</span>
          <span className="text-[11px] text-on-surface-variant font-medium">
            {new Date(ticket.timestamp * 1000).toLocaleString()}
          </span>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 ${isResolved
              ? "bg-secondary/15 text-secondary border border-secondary/30"
              : "bg-surface-container-highest text-on-surface-variant"
            }`}
        >
          <span className="material-symbols-outlined text-[14px]">
            {isResolved ? "check_circle" : "pending"}
          </span>
          {isResolved ? "Agronomist Verified" : "Pending Review"}
        </span>
      </div>

      {/* Grid: Image & Info */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
        {/* Thumbnail */}
        {ticket.image ? (
          <div className="sm:col-span-1 aspect-square rounded-lg overflow-hidden border border-outline-variant/20 bg-black/10">
            <img src={getImageUrl(ticket.image)} alt="Crop Scan" className="w-full h-full object-cover" />
          </div>
        ) : null}

        {/* Info */}
        <div className={`${ticket.image ? "sm:col-span-2" : "sm:col-span-3"} space-y-2 text-xs`}>
          <div>
            <span className="text-on-surface-variant font-medium">Crop:</span>{" "}
            <strong className="text-on-surface">{orig.crop?.label || orig.crop || "—"}</strong>
          </div>
          <div>
            <span className="text-on-surface-variant font-medium">Stage:</span>{" "}
            <strong className="text-on-surface">{orig.stage?.label || orig.stage || "—"}</strong>
          </div>
          <div>
            <span className="text-on-surface-variant font-medium">Category:</span>{" "}
            <strong className="text-on-surface">{orig.pest?.category || orig.category || "—"}</strong>
          </div>
          <div>
            <span className="text-on-surface-variant font-medium">Disease:</span>{" "}
            <strong className="text-on-surface">
              {isResolved
                ? (ticket.validated_prediction?.disease || orig.pest?.disease || orig.disease || orig.diseaseLabel || "None")
                : (orig.pest?.disease || orig.disease || orig.diseaseLabel || "None")}
            </strong>
          </div>
          <div>
            <span className="text-on-surface-variant font-medium">Pest:</span>{" "}
            <strong className="text-on-surface">
              {isResolved
                ? (ticket.validated_prediction?.pest || orig.pest?.pest || orig.pestLabel || "None")
                : (orig.pest?.pest || orig.pestLabel || "None")}
            </strong>
          </div>
          {isResolved && (
            <div className="pt-2 text-right">
              <span className="text-primary font-bold text-xs hover:underline flex items-center justify-end gap-1">
                <span className="material-symbols-outlined text-[16px]">compare</span>
                Compare Prediction
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
